import { Button, Empty, Input, Pagination, Select, Spin } from "@arco-design/web-react";
import { ArrowDown, ArrowUp, MessageCircle, Phone, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { canAccessCrmOperation, type PermissionUser } from "../../../lib/auth";
import { SEARCH_HISTORY_KEYS, rememberSearchHistory } from "../../../lib/searchHistoryStorage";
import {
  formatContactArea,
  formatContactOwners,
} from "../service/crmContactWorkbenchService";
import type {
  ContactWorkbenchItem,
  ContactWorkbenchOrganizationOption,
} from "../types";
import type { useCrmContactWorkbenchRuntime } from "../runtime/useCrmContactWorkbenchRuntime";

type ContactWorkbenchRuntime = ReturnType<typeof useCrmContactWorkbenchRuntime>;
type SortDirection = "asc" | "desc";

const MOBILE_CONTACT_SORT_OPTIONS = [
  { label: "动态", field: "record_count" },
  { label: "最近动态", field: "last_contact_at" },
  { label: "礼品", field: "gift_count" },
  { label: "新增时间", field: "created_at" },
] as const;

/** 统一封装移动断点监听，与商机工作台 760px 口径一致。 */
export function useIsMobileViewport(maxWidth = 760) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, [maxWidth]);

  return isMobile;
}

/** 移动端联系人筛选栏；只保留关键词与学校。 */
export function MobileContactFilterBar({
  runtime,
  canCreate,
  onCreate,
  onReset,
  schoolOptions,
  schoolLoading,
  onSearchSchools,
  onSchoolChange,
}: {
  runtime: ContactWorkbenchRuntime;
  canCreate: boolean;
  onCreate: () => void;
  onReset: () => void;
  schoolOptions: ContactWorkbenchOrganizationOption[];
  schoolLoading: boolean;
  onSearchSchools: (keyword: string) => void | Promise<void>;
  onSchoolChange: (values: (string | number)[]) => void;
}) {
  /** 移动查询只提交关键词与学校，并保留当前「我的/全部」范围。 */
  function handleMobileSearch() {
    rememberSearchHistory(SEARCH_HISTORY_KEYS.contactKeyword, runtime.draftFilters.keyword ?? "");
    runtime.handleSearch({
      viewScope: runtime.draftFilters.viewScope ?? "mine",
      keyword: runtime.draftFilters.keyword,
      orgIds: runtime.draftFilters.orgIds,
      orgNames: runtime.draftFilters.orgNames,
    });
  }

  return (
    <section
      className="crm-contact-workbench-mobile-filter-panel"
      role="search"
      aria-label="移动端联系人筛选"
    >
      <div className="crm-contact-workbench-mobile-toolbar">
        <div className="crm-contact-workbench-mobile-toolbar-left">
          <h1>联系人列表</h1>
        </div>
        {canCreate ? (
          <Button type="primary" size="mini" icon={<Plus size={14} />} onClick={onCreate}>
            新增
          </Button>
        ) : null}
      </div>
      <div className="crm-contact-workbench-mobile-filter-fields">
        <div className="crm-contact-workbench-mobile-filter-pair">
          <Input
            allowClear
            size="small"
            prefix={<Search size={15} />}
            aria-label="关键词"
            placeholder="联系人/手机/微信/学校"
            value={runtime.draftFilters.keyword ?? ""}
            onChange={(keyword) => runtime.setDraftFilters((current) => ({ ...current, keyword }))}
            onPressEnter={handleMobileSearch}
          />
          <Select
            allowClear
            showSearch
            mode="multiple"
            size="small"
            filterOption={false}
            loading={schoolLoading}
            maxTagCount={1}
            aria-label="学校"
            placeholder="学校"
            value={runtime.draftFilters.orgIds ?? []}
            onFocus={() => void onSearchSchools("")}
            onSearch={(keyword) => void onSearchSchools(keyword)}
            onChange={(values) => onSchoolChange(Array.isArray(values) ? values : [])}
          >
            {schoolOptions.map((option) => (
              <Select.Option key={option.id} value={option.id}>
                {option.orgName}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="crm-contact-workbench-mobile-filter-actions">
          <Button type="text" size="mini" onClick={onReset}>
            重置
          </Button>
          <Button type="primary" size="mini" onClick={handleMobileSearch} aria-label="查询联系人">
            查询
          </Button>
        </div>
      </div>
    </section>
  );
}

/** 移动端排序条：动态 / 最近动态 / 礼品 / 新增时间，对齐桌面可排列表头。 */
export function MobileContactSortBar({
  sortField,
  sortDirection,
  onSort,
}: {
  sortField: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
}) {
  return (
    <nav
      className="crm-contact-workbench-mobile-sort-bar"
      aria-label="联系人排序"
      data-testid="contact-mobile-sort-bar"
    >
      {MOBILE_CONTACT_SORT_OPTIONS.map((option) => (
        <MobileSortableHeader
          key={option.field}
          label={option.label}
          field={option.field}
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ))}
    </nav>
  );
}

/** 移动端排序项：双圆钮指定升/降序。 */
function MobileSortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: string;
  activeField?: string;
  activeDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
}) {
  const active = activeField === field;
  return (
    <div className="crm-contact-workbench-sort-header">
      <span>{label}</span>
      <span className="crm-contact-workbench-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}

/** 移动端联系人列表卡片。 */
export function MobileContactList({
  runtime,
  permissionUser,
  latestRecordLabels,
  latestRecordLabelsLoading,
  onEdit,
  onViewRecords,
  onAddRecord,
}: {
  runtime: ContactWorkbenchRuntime;
  permissionUser?: PermissionUser | null;
  latestRecordLabels: Record<number, string>;
  latestRecordLabelsLoading: boolean;
  onEdit: (item: ContactWorkbenchItem) => void;
  onViewRecords: (item: ContactWorkbenchItem) => void;
  onAddRecord: (item: ContactWorkbenchItem) => void;
}) {
  return (
    <div className="crm-contact-workbench-mobile-list" data-testid="contact-mobile-list">
      {runtime.items.length > 0 ? (
        <Spin loading={runtime.loading}>
          <div className="crm-contact-workbench-mobile-card-list" role="list">
            {runtime.items.map((item) => (
              <MobileContactCard
                key={item.id}
                item={item}
                permissionUser={permissionUser}
                labelText={
                  latestRecordLabels[item.id] ?? (latestRecordLabelsLoading ? "加载中…" : "-")
                }
                onEdit={onEdit}
                onViewRecords={onViewRecords}
                onAddRecord={onAddRecord}
              />
            ))}
          </div>
        </Spin>
      ) : runtime.loading ? (
        <div
          className="crm-contact-workbench-mobile-list-status"
          data-testid="contact-mobile-list-loading"
          role="status"
          aria-live="polite"
          aria-label="正在加载数据"
        >
          <Spin tip="正在加载数据" />
        </div>
      ) : (
        <Empty description="暂无符合条件的联系人" />
      )}
      {runtime.loading && runtime.items.length === 0 ? null : (
        <Pagination
          className="crm-contact-workbench-mobile-pagination"
          total={runtime.total}
          current={runtime.page}
          pageSize={runtime.pageSize}
          showTotal={(total) => `共 ${total} 条`}
          sizeCanChange={false}
          bufferSize={1}
          onChange={runtime.handlePageChange}
        />
      )}
    </div>
  );
}

/** 单条联系人移动卡片：展示桌面列表核心字段，并提供修改/查看动态/新增动态。 */
function MobileContactCard({
  item,
  permissionUser,
  labelText,
  onEdit,
  onViewRecords,
  onAddRecord,
}: {
  item: ContactWorkbenchItem;
  permissionUser?: PermissionUser | null;
  labelText: string;
  onEdit: (item: ContactWorkbenchItem) => void;
  onViewRecords: (item: ContactWorkbenchItem) => void;
  onAddRecord: (item: ContactWorkbenchItem) => void;
}) {
  const canEdit = canAccessCrmOperation(permissionUser, "crm.contact:access", Boolean(item.canEdit));
  const canRecord = canAccessCrmOperation(
    permissionUser,
    "crm.contact:record",
    Boolean(item.canRecord),
  );
  const canOpenRecords = item.canOpenContact !== false;
  const orgUnit = item.mainOrgUnitPathName?.trim() || item.mainOrgUnitName?.trim() || "";
  const owners = formatContactOwners(item.primaryOwnerUserNames ?? []);
  const sharedCount = item.sharedOwnerUserNames?.length ?? 0;
  const ownerText =
    sharedCount > 0 ? `${owners} · 共享 ${sharedCount}` : owners === "未分配" ? "" : owners;
  const mobile = item.mobile?.trim() || "";
  const wechat = item.wechat?.trim() || "";
  const telHref = resolveContactTelHref(mobile, item.canViewSensitiveContactInfo !== false);
  const areaText = formatContactArea(item.province, item.city, item.district);
  const areaDisplay = areaText === "-" ? "" : areaText;
  const labelDisplay = !labelText || labelText === "-" || labelText === "加载中…" ? "" : labelText;
  const latestType = item.latestRecordTypeLabel?.trim() || "";
  const latestAt = item.latestRecordAt?.trim() || "";
  const latestSummary =
    item.latestRecordMeetingName?.trim() || item.latestRecordContent?.trim() || "";
  const hasLatest = Boolean(latestType || latestAt || latestSummary);

  return (
    <article className="crm-contact-workbench-mobile-card" role="listitem">
      <header className="crm-contact-workbench-mobile-card-header">
        <div className="crm-contact-workbench-mobile-card-title">
          <strong className="crm-contact-workbench-mobile-card-name" title={item.contactName}>
            {item.contactName}
          </strong>
          {item.positionName?.trim() ? (
            <span className="crm-contact-workbench-mobile-card-position">{item.positionName}</span>
          ) : null}
        </div>
        {canOpenRecords ? (
          <button
            type="button"
            className="crm-contact-workbench-mobile-card-count"
            aria-label={`查看${item.contactName}的动态记录，共 ${item.recordCount} 条`}
            onClick={() => onViewRecords(item)}
          >
            {item.recordCount}条动态
          </button>
        ) : item.recordCount > 0 ? (
          <span className="crm-contact-workbench-mobile-card-count is-static">
            {item.recordCount}条动态
          </span>
        ) : null}
      </header>

      <div className="crm-contact-workbench-mobile-card-body">
        <div className="crm-contact-workbench-mobile-card-org">
          <p className="crm-contact-workbench-mobile-card-school" title={item.orgName || undefined}>
            {item.orgName || "未填写学校"}
          </p>
          {orgUnit ? (
            <p className="crm-contact-workbench-mobile-card-unit" title={orgUnit}>
              {orgUnit}
            </p>
          ) : null}
        </div>

        {ownerText || areaDisplay || labelDisplay ? (
          <div className="crm-contact-workbench-mobile-card-meta" aria-label="联系人摘要">
            {ownerText ? (
              <span className="crm-contact-workbench-mobile-card-chip is-owner">{ownerText}</span>
            ) : null}
            {areaDisplay ? (
              <span className="crm-contact-workbench-mobile-card-chip is-area">{areaDisplay}</span>
            ) : null}
            {labelDisplay ? (
              <span className="crm-contact-workbench-mobile-card-chip is-emphasis">{labelDisplay}</span>
            ) : null}
          </div>
        ) : null}

        {mobile || wechat ? (
          <div className="crm-contact-workbench-mobile-card-contact">
            {mobile ? (
              telHref ? (
                <a
                  className="crm-contact-workbench-mobile-card-phone"
                  href={telHref}
                  aria-label={`拨打 ${mobile}`}
                  title={`拨打 ${mobile}`}
                >
                  <Phone size={13} aria-hidden strokeWidth={2.25} />
                  <span>{mobile}</span>
                </a>
              ) : (
                <span className="crm-contact-workbench-mobile-card-phone" title={mobile}>
                  <Phone size={13} aria-hidden strokeWidth={2.25} />
                  <span>{mobile}</span>
                </span>
              )
            ) : null}
            {wechat ? (
              <span className="crm-contact-workbench-mobile-card-wechat" title={wechat}>
                <MessageCircle size={13} aria-hidden strokeWidth={2.25} />
                <span>{wechat}</span>
              </span>
            ) : null}
          </div>
        ) : null}

        {hasLatest ? (
          canOpenRecords ? (
            <button
              type="button"
              className="crm-contact-workbench-mobile-card-latest is-clickable"
              aria-label={`查看${item.contactName}的动态记录`}
              onClick={() => onViewRecords(item)}
            >
              {latestType || latestAt ? (
                <div className="crm-contact-workbench-mobile-card-latest-meta">
                  {latestType ? <span>{latestType}</span> : null}
                  {latestAt ? <time dateTime={latestAt}>{latestAt}</time> : null}
                </div>
              ) : null}
              {latestSummary ? (
                <p className="crm-contact-workbench-mobile-card-latest-content" title={latestSummary}>
                  {latestSummary}
                </p>
              ) : null}
            </button>
          ) : (
            <div className="crm-contact-workbench-mobile-card-latest">
              {latestType || latestAt ? (
                <div className="crm-contact-workbench-mobile-card-latest-meta">
                  {latestType ? <span>{latestType}</span> : null}
                  {latestAt ? <time dateTime={latestAt}>{latestAt}</time> : null}
                </div>
              ) : null}
              {latestSummary ? (
                <p className="crm-contact-workbench-mobile-card-latest-content" title={latestSummary}>
                  {latestSummary}
                </p>
              ) : null}
            </div>
          )
        ) : null}
      </div>

      <div className="crm-contact-workbench-mobile-card-footer">
        {canEdit ? (
          <button
            type="button"
            className="crm-contact-workbench-mobile-card-action is-secondary"
            onClick={() => onEdit(item)}
          >
            修改
          </button>
        ) : null}
        {canRecord ? (
          <button
            type="button"
            className="crm-contact-workbench-mobile-card-action is-primary"
            onClick={() => onAddRecord(item)}
          >
            新增动态
          </button>
        ) : null}
      </div>
    </article>
  );
}

/** 可拨号时生成 tel: 链接；脱敏或无权限时返回 null，避免拨打残缺号码。 */
export function resolveContactTelHref(mobile: string, canDial: boolean): string | null {
  if (!canDial) {
    return null;
  }
  const trimmed = mobile.trim();
  if (!trimmed || /[*＊]/.test(trimmed)) {
    return null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 7) {
    return null;
  }
  return `tel:${digits}`;
}
